import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  TextField,
  ThemeProvider,
  createTheme,
  CircularProgress,
  Divider,
  Alert,
  Tooltip,
  Zoom,
  Fade,
  Checkbox,
  FormControlLabel,
  Stack,
  Chip
} from '@mui/material';

import VoteResultsCard from '../Components/VoteResults';

import {
  Check as CheckIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import FactCheckerApi from '../apis/fact_checker';
import { Stepper, Step, StepLabel, StepContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CategoryIcon from '@mui/icons-material/Category';
import EventIcon from '@mui/icons-material/Event';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InfoIcon from '@mui/icons-material/Info';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import TurnstileWidget from '../Components/TurnstileWidget';
import { useCallback, useRef } from 'react';
import ChromeExtensionGuide from '../Components/ChromeExtensionGuide';
import ChromeExtensionBanner from '../Components/ChromeExtensionBanner';
import MagicLoadingAnimation from '../Components/MagicLoadingAnimation';
import CredibilityMark from '../Components/CredibilityMarker';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import BouncingCTA from '../Components/BouncingButton/BouncingCTA';
import Threads from '../Components/Threads/Threads';



// Custom theme to match the design
const theme = createTheme({
  palette: {
    primary: {
      main: '#4361ee',
    },
    secondary: {
      main: '#4caf50',
    },
    background: {
      default: '#1e2d58',
      paper: '#ffffff',
    },
    analysis: {
      red: '#ffcdd2',
      yellow: '#fff9c4',
      green: '#c8e6c9',
    },
    woo: {
      purple: '#9c27b0',
      blue: '#3f51b5',
      teal: '#009688',
      gold: '#ffc107',
    }
  },
  typography: {
    h2: {
      fontWeight: 700,
      color: '#ffffff',
      fontSize: '2.5rem',
    },
    subtitle1: {
      color: '#ffffff',
      fontSize: '1.1rem',
      marginBottom: '2rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      marginBottom: '1rem',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 50,
          padding: '12px 40px',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '1.25rem',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'white',
          color: 'rgba(0, 0, 0, 0.87)',
          maxWidth: 300,
          fontSize: '0.9rem',
          border: '1px solid #dadde9',
          boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
          padding: '16px',
          borderRadius: '12px',
        },
        arrow: {
          color: 'white'
        }
      }
    }
  },
});


const DialogBubbleContent = ({ result }) => {
  // Determine status icon and color based on result
  let iconColor = '#f44336'; // Red for false
  let statusText = 'False';
  let iconSymbol = '✖';

  if (result.result === 'true') {
    iconColor = '#4caf50'; // Green for true
    statusText = 'True';
    iconSymbol = '✓';
  } else if (result.result === 'unverifiable') {
    iconColor = '#ff9800'; // Yellow/Orange for unverifiable
    statusText = 'Unverifiable';
    iconSymbol = '?';
  }

  // Set default values for agrees/disagrees
  const agrees = result.upvotes || 0;
  const disagrees = result.downvotes || 0;

  // Calculate percentages for bar widths
  const total = agrees + disagrees;
  const agreePercent = total > 0 ? (agrees / total) * 100 : 50;
  const disagreePercent = total > 0 ? (disagrees / total) * 100 : 50;

  return (

    <Box sx={{ width: '100%' }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        mb: 1.5,
        pb: 1.5,
        borderBottom: '1px solid #eee'
      }}>
        <Box sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          bgcolor: iconColor,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'white',
          fontWeight: 'bold',
          mr: 1.5
        }}>
          {iconSymbol}
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          {statusText}
        </Typography>

        {/* Integrated Vote Results */}
        <Box sx={{ ml: 'auto', display: 'flex', flexDirection: 'column', width: '180px' }}>
          <Typography variant="caption" sx={{ mb: 0.5, color: '#555' }}>
            Vote Distribution:
          </Typography>

          {/* Bar for agreement and disagreement */}
          <Box sx={{
            position: 'relative',
            width: '100%',
            height: '10px',
            borderRadius: '5px',
            overflow: 'hidden',
            mb: 1,
            bgcolor: '#f0f0f0' // Light gray background for empty bar
          }}>
            {/* Blue bar for agrees */}
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                height: '100%',
                bgcolor: '#2196f3',
                width: `${agreePercent}%`
              }}
            >
              {agrees > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    right: 4,
                    top: -2,
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '9px'
                  }}
                >
                  {agrees}
                </Typography>
              )}
            </Box>

            {/* Purple bar for disagrees */}
            <Box
              sx={{
                position: 'absolute',
                left: `${agreePercent}%`,
                top: 0,
                height: '100%',
                bgcolor: '#9c27b0',
                width: `${disagreePercent}%`
              }}
            >
              {disagrees > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    right: 4,
                    top: -2,
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '9px'
                  }}
                >
                  {disagrees}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Labels below the bar */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: '#2196f3', fontWeight: 'medium' }}>
              Agree: {agrees}
            </Typography>
            <Typography variant="caption" sx={{ color: '#9c27b0', fontWeight: 'medium' }}>
              Disagree: {disagrees}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Typography variant="body2">
        {result.reason}
      </Typography>
    </Box>
  );
};

// Result Item component to display each analyzed segment with hover tooltip
const ResultItem = ({ result }) => {
  const [isHovered, setIsHovered] = useState(false);

  // Determine background color based on result
  let bgColor = 'transparent';
  let borderColor = 'transparent';

  if (result.result === 'false') {
    bgColor = theme.palette.analysis.red;
    borderColor = '#ef9a9a';
  } else if (result.result === 'unverifiable') {
    bgColor = theme.palette.analysis.yellow;
    borderColor = '#fff59d';
  } else if (result.result === 'true') {
    bgColor = theme.palette.analysis.green;
    borderColor = '#a5d6a7';
  }

  return (
    <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid #eee' }}>
      <Tooltip
        title={<DialogBubbleContent result={result} />}
        arrow
        placement="bottom-start"
        TransitionComponent={Zoom}
        enterDelay={200}
        leaveDelay={300}
        TransitionProps={{ timeout: 300 }}
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 10],
              },
            },
          ],
        }}
      >
        <Box
          sx={{
            bgcolor: bgColor,
            p: 2,
            borderRadius: 2,
            mb: 1,
            border: '2px solid',
            borderColor: isHovered ? borderColor : 'transparent',
            cursor: 'pointer',
            position: 'relative',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)',
              transform: 'translateY(-2px)',
              borderColor: borderColor
            },
            '&::after': isHovered ? {
              content: '""',
              position: 'absolute',
              top: '100%',
              left: '20px',
              width: '10px',
              height: '10px',
              bgcolor: borderColor,
              transform: 'rotate(45deg) translateY(-50%)',
              transition: 'all 0.2s ease-in-out'
            } : {}
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Typography variant="body1">{result.text}</Typography>

          {/* Small indicator that shows when hovering */}
          <Fade in={isHovered}>
            <Box sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '4px',
              px: 1,
              py: 0.5,
              fontSize: '0.7rem',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              Hover for details
            </Box>
          </Fade>
        </Box>
      </Tooltip>
    </Box>
  );
};
const TextTrustworthinessDetector = () => {
  const [input, setInput] = useState('');
  const [isUrl, setIsUrl] = useState(false);
  const [analyzed, setAnalyzed] = useState(false);
  const [analysisResults, setAnalysisResults] = useState([]);
  const [credibilityScore, setCredibilityScore] = useState(0);
  const [AIScore, setAIScore] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [allowPublicVoting, setAllowPublicVoting] = useState(false);
  const [token, setToken] = useState(null);
  const [showTurnstile, setShowTurnstile] = useState(false);
  const [sources, setSources] = useState([]);
  // Add a new state for tracking expanded sources
  const [showAllSources, setShowAllSources] = useState(false);
  const [debouncedInput, setDebouncedInput] = useState('');



  // New state for website information
  const [websiteInfo, setWebsiteInfo] = useState(null);

  const [fetchingWebsiteInfo, setFetchingWebsiteInfo] = useState(false);

  const handleVerify = useCallback((token) => {
    setToken(token);
  }, []);

  // Progress simulation for loading animation
  useEffect(() => {
    let timer;
    if (loading) {
      setLoadingProgress(0);
      timer = setInterval(() => {
        setLoadingProgress(prev => {
          // Slow down as we approach 90%
          const increment = prev < 50 ? 4 : prev < 80 ? 2 : 1;
          const newProgress = Math.min(prev + increment, 95);
          return newProgress;
        });
      }, 300);
    } else if (analyzed) {
      setLoadingProgress(100);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [loading, analyzed]);


  const isFirstRun = useRef(true);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    console.log("token");
    if (!token) return;
    setLoading(true);

    const apiMethod = isUrl
      ? FactCheckerApi.checkUrl(input, allowPublicVoting, token)
      : FactCheckerApi.checkText(input, allowPublicVoting, token);

    apiMethod.then(response => {
      const sortedResponse = response.data.response.sort((a, b) => {
        if (a.index !== b.index) {
          return a.index - b.index;
        }
        return a.sub_index - b.sub_index;
      });
      setSources(response.data.resources);
      setAIScore(Math.round(response.data.ai_score));
      setAnalysisResults(sortedResponse);
      setCredibilityScore(response.data.score);
      setAnalyzed(true);
      setLoading(false);
      setToken(null);
      setShowTurnstile(false);
      // Reset sources expanded state when new analysis is done
      setShowAllSources(false);
    }).catch(error => {
      console.error("Error analyzing content:", error);
      setError("An error occurred while analyzing the content. Please try again.");
      setLoading(false);
      setToken(null);
      setShowTurnstile(false);
    });

  }, [token]);

  // Function to fetch website information
  const fetchWebsiteInfo = useCallback(async (url) => {
    if (!url || !isValidUrl(url)) return;

    setFetchingWebsiteInfo(true);
    setWebsiteInfo(null);

    try {
      const response = await FactCheckerApi.getWebsiteInfo(url);
      setWebsiteInfo(response.data);
    } catch (error) {
      console.error("Error fetching website information:", error);
      // Don't show error to user, just silently fail for this feature
    } finally {
      setFetchingWebsiteInfo(false);
    }
  }, []);

  // Function to toggle showing all sources
  const toggleShowAllSources = () => {
    setShowAllSources(prev => !prev);
  };

  // Debounce the website info fetch to prevent too many API calls
  useEffect(() => {
    if (isUrl && input.trim()) {
      const timer = setTimeout(() => {
        fetchWebsiteInfo(input);
      }, 800); // Wait 800ms after typing stops

      return () => clearTimeout(timer);
    } else {
      setWebsiteInfo(null);
    }
  }, [input, isUrl, fetchWebsiteInfo]);

  const handleInputChange = (event) => {
    const newInput = event.target.value;
    setInput(newInput);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      // Auto-detect if input appears to be a URL
      const newIsUrl = isValidUrl(input);
      setIsUrl(newIsUrl);
      setAnalyzed(false);
      setError(null);
      setDebouncedInput(input);
    }, 800); // 500ms debounce time

    return () => clearTimeout(timer);
  }, [input]);

  const isValidUrl = (text) => {
    try {
      const regex = /^(https?:\/\/)?(www\.)?([a-z0-9-]+\.)+[a-z]{2,}(:\d+)?(\/.*)?$/i;
      return regex.test(text);
    } catch (e) {
      return false;
    }
  };

  const handleVotingCheckboxChange = (event) => {
    setAllowPublicVoting(event.target.checked);
  };

  const handleDetect = () => {
    if (!input.trim()) {
      setError('Please enter some text or a valid URL');
      return;
    }

    setError(null);
    setShowTurnstile(true);

  };

  const getPlaceholderText = () => {
    return "Enter text or paste a URL to check its trustworthiness...";
  };

  const shouldShowMultilineInput = () => {
    return !isUrl || input === '';
  };

  const handleTutorialOpen = () => {
    setTutorialOpen(true);
  }

  return (
    <ThemeProvider theme={theme}>
      <ChromeExtensionBanner handleTutorialOpen={handleTutorialOpen}></ChromeExtensionBanner>
      <ChromeExtensionGuide setTutorialOpen={setTutorialOpen} tutorialOpen={tutorialOpen} />
      <Box
        sx={{
          // bgcolor: 'background.default'
          backgroundImage: 'linear-gradient(to bottom right,rgba(2, 26, 2, 0.67),rgba(10, 41, 2, 0.42))',
          minHeight: '100vh',
          py: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          zIndex: 1, // above the background threads
          position: 'relative',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            align="center"
            gutterBottom
            sx={{ fontSize: '3rem', color: 'white', fontWeight: 'bold' }} // h2 is usually 2.5rem
          >
            Is This Information Trustworthy?
          </Typography>

          <Typography
            variant="h4"
            align="center"
            sx={{ fontSize: '2rem', color: 'white', fontWeight: 'bold' }} // h4 is usually 2rem
          >
            {/* {isUrl
              ? 'Enter a website URL to analyze its content for trustworthiness and get detailed fact checks.'
              : 'Paste text to get a trust score and a simple explanation — so you can verify before you believe.'} */}
            {/* {isUrl
              ? 'Copy and paste an excerpt from a news story, article, or even a link that seems misleading or needs verification.'
              : 'Copy and paste an excerpt from a news story, article, or even a link that seems misleading or needs verification.'} */}
            Copy and paste an excerpt from a news story, article, or even a link that seems misleading or needs verification
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2, mb: 1 }}>
            <Typography
              variant="body2"
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                p: 1,
                borderRadius: 2,
                color: 'white'
              }}
            >
              {isUrl ? 'URL detected' : 'Text input'}
            </Typography>
          </Box>

          <Paper elevation={3} sx={{ p: 0, mt: 2, overflow: 'hidden' }}>
            <TextField
              fullWidth
              // multiline={shouldShowMultilineInput()}
              multiline={true}
              rows={shouldShowMultilineInput() ? 8 : 1}
              placeholder={getPlaceholderText()}
              variant="outlined"
              value={input}
              onChange={handleInputChange}
              InputProps={{
                sx: {
                  p: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }
              }}
            />
          </Paper>

          {/* Website Information Display */}
          {isUrl && input.trim() && (
            <Paper
              elevation={2}
              sx={{
                p: 2,
                mt: 2,
                borderLeft: fetchingWebsiteInfo ? '4px solid #FFA726' : '4px solid #4caf50',
                bgcolor: fetchingWebsiteInfo ? 'rgba(255, 167, 38, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                transition: 'background-color 0.3s, border-left-color 0.3s'
              }}
            >
              {fetchingWebsiteInfo ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={16} sx={{ mr: 1, color: '#FFA726' }} />
                  <Typography variant="body2" color="white">
                    Fetching website information...
                  </Typography>
                </Box>
              ) : websiteInfo ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" color="white">
                      {websiteInfo.title || new URL(input).hostname}
                    </Typography>
                  </Box>

                  {websiteInfo.description && (
                    <Typography variant="body2" sx={{ mt: 1 }} color="white">
                      {websiteInfo.description}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <InfoIcon fontSize="small" sx={{ mr: 1, color: 'white' }} />
                  <Typography variant="body2" color="white">
                    No website information available
                  </Typography>
                </Box>
              )}
            </Paper>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={allowPublicVoting}
                onChange={handleVotingCheckboxChange}
                sx={{
                  color: '#ccc', // Unchecked color
                  '&.Mui-checked': {
                    color: '#fff', // Checked color
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)', // Optional hover styling
                  },
                }}
              />
            }
            label={
              <Typography variant="h6" sx={{ color: 'white' }}>
                Allow this analysis to be shared for public voting to against misinformation
              </Typography>
            }
            sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Stack direction="column" spacing={2}>
              {showTurnstile && (
                <TurnstileWidget
                  onVerify={handleVerify}
                />
              )}
              <Button
                variant="contained"
                color="secondary"
                size="large"
                startIcon={<SearchIcon />}
                onClick={handleDetect}
                disabled={!input.trim() || loading}
                sx={{
                  minWidth: 180,
                  bgcolor: loading ? theme.palette.woo.purple : '#4caf50',
                  '&:hover': {
                    bgcolor: loading ? theme.palette.woo.purple : '#43a047',
                  },
                  '&.Mui-disabled': {
                    bgcolor: '#a5d6a7',
                    color: 'white',
                  }
                }}
              >
                {loading ? 'Channeling Magic...' : 'Analyze'}
              </Button>
            </Stack>
          </Box>

          {loading && (
            <Paper
              elevation={3}
              sx={{
                p: 2,
                mt: 4,
                bgcolor: '#1e2d5811',
                backgroundImage: 'radial-gradient(to bottom right,rgba(2, 26, 2, 0.67),rgba(10, 41, 2, 0.42)'
                // backgroundImage: 'radial-gradient(circle at center, #2e3d78 0%, #1e2d58 100%)'
              }}
            >
              <MagicLoadingAnimation progress={loadingProgress} />
            </Paper>
          )}

          {analyzed && !loading && (
            <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: '200px' }}>
                  <Typography variant="h5">Analysis Results:</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>Red:</strong> False information
                    <br />
                    <strong>Green:</strong> Verified information
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                    Score is calculated based on how many sentences are verified as true or false.
                    {/* <br />
                    <br />
                    Our AI model and Gemini may give different credibility results because they use different methods.
                    <br />
                    Our AI analyzes text patterns based on past data while Gemini searches the web in real time
                    to find supporting or contradicting evidence. */}
                  </Typography>
                </Box>
                {/* <CredibilityMark score={AIScore} title="Dunder Debunker" />
                <CredibilityMark score={credibilityScore} title="Gemini Score" /> */}
                {/* <CredibilityMark
                  score={AIScore}
                  title="Dunder Debunker"
                  imageSrc="/images/dunderdebunker.png"
                  imageAlt="Dunder Debunker Logo"
                />

                <CredibilityMark
                  score={credibilityScore}
                  title="Gemini Score"
                  imageSrc="/images/Geminilogo.png"
                  imageAlt="Gemini Logo"
                /> */}
                {/* <Tooltip
                  title={
                    <Typography sx={{ fontSize: '0.875rem', whiteSpace: 'normal' }}>
                      This result comes from our in-house AI model trained by fine-tuning a BERT model on credibility-labeled news data from Kaggle.
                    </Typography>
                  }
                  arrow
                  placement="top"
                >
                  <Box>
                    <CredibilityMark
                      score={AIScore}
                      title="Dunder Debunker"
                      imageSrc="/images/dunderdebunker.png"
                      imageAlt="Dunder Debunker Logo"
                    />
                  </Box>
                </Tooltip> */}

                <Tooltip
                  title={
                    <Typography sx={{ fontSize: '0.875rem', whiteSpace: 'normal' }}>
                      Google’s Gemini checks real-time web sources to provide evidence for or against the claim and help you assess credibility based on current information.
                    </Typography>
                  }
                  arrow
                  placement="top"
                >
                  <Box>
                    <CredibilityMark
                      score={credibilityScore}
                      title="Gemini Score"
                      imageSrc="/images/Geminilogo.png"
                      imageAlt="Gemini Logo"
                    />
                  </Box>
                </Tooltip>
              </Box>

              {sources && sources.length > 0 && (
                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                  <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
                    Sources referenced in the analysis:
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {/* Always show first 2 sources */}
                    {sources.slice(0, 2).map((source, idx) => (
                      <Box
                        key={idx}
                        component="a"
                        href={source.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 0.5,
                          textDecoration: 'none',
                          color: 'inherit',
                          '&:hover': {
                            textDecoration: 'underline',
                            color: 'primary.main'
                          }
                        }}
                      >
                        <Box
                          component="img"
                          src={`https://www.google.com/s2/favicons?domain=${source.source_url}`}
                          alt={`${source.source_url} favicon`}
                          sx={{ width: 16, height: 16, mr: 1 }}
                        />
                        <Typography variant="caption">
                          {source.source_title || source.source_url}
                        </Typography>
                      </Box>
                    ))}

                    {/* Show additional sources if expanded */}
                    {showAllSources && sources.length > 2 && (
                      <Box sx={{ mt: 1 }}>
                        {sources.slice(2).map((source, idx) => (
                          <Box
                            key={idx + 2}
                            component="a"
                            href={source.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mb: 0.5,
                              textDecoration: 'none',
                              color: 'inherit',
                              '&:hover': {
                                textDecoration: 'underline',
                                color: 'primary.main'
                              }
                            }}
                          >
                            <Box
                              component="img"
                              src={`https://www.google.com/s2/favicons?domain=${source.source_url}`}
                              alt={`${source.source_url} favicon`}
                              sx={{ width: 16, height: 16, mr: 1 }}
                            />
                            <Typography variant="caption">
                              {source.source_title || source.source_url}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Show/Hide toggle button if there are more than 2 sources */}
                    {sources.length > 2 && (
                      <Button
                        onClick={toggleShowAllSources}
                        sx={{
                          mt: 1,
                          fontSize: '0.75rem',
                          p: 0,
                          minWidth: 0,
                          color: 'primary.main',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline'
                          }
                        }}
                        endIcon={showAllSources ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                      >
                        {showAllSources ? `Hide ${sources.length - 2} sources` : `Show ${sources.length - 2} more sources`}
                      </Button>
                    )}
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mt: 2 }}>
                {analysisResults.map((result, index) => (
                  <ResultItem key={`${result.index}-${result.sub_index}`} result={result} />
                ))}
              </Box>

              {allowPublicVoting && (
                <Box sx={{ mt: 4, p: 2, bgcolor: 'rgba(0, 128, 0, 0.1)', borderRadius: 2 }}>
                  <Typography variant="body2" color="primary">
                    <InfoIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                    This analysis has been shared for public voting against misinformation. Thank you for contributing!
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </Container>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'stretch',
            gap: 4, // spacing between cards
            flexWrap: 'wrap', // allow wrapping on smaller screens
            mt: 4, // top margin
          }}
        >
          <BouncingCTA
            text="Check out our community flagging page to add your salt to the fight against misinformation!"
            link="/community-flagging"
            linkText="Join the effort"
            bgImage="/images/navcards/a_flag.png"
          />
          <BouncingCTA
            text="Dive into the game and put your knowledge to the test and Defeat ECHO!"
            link="/defence-game"
            linkText="Play the Game"
            bgImage="/images/navcards/gameplay.png"
          />
        </Box>
      </Box>
      {/* <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 0, // behind the main content
        }}
      >
        <Threads amplitude={1.5} distance={0.1} enableMouseInteraction={false} />
      </Box> */}
    </ThemeProvider>
  );
};
export default TextTrustworthinessDetector;